/* global edacpFullSiteScanApp, wp */
import { debug } from './helpers';
import { showNotice } from '../common/helpers';
import { fillFailsAccordion } from './modules/failAccordion';
import { __, sprintf, setLocaleData } from '@wordpress/i18n';

// Ensure translations are loaded properly
if ( typeof wp !== 'undefined' && wp.i18n ) {
	const localeData = wp.i18n.getLocaleData ? wp.i18n.getLocaleData( 'accessibility-checker-pro' ) : {};
	setLocaleData( localeData, 'accessibility-checker-pro' );
}

const API_URL = edacpFullSiteScanApp.edacpApiUrl;
const WP_REST_NONCE = edacpFullSiteScanApp.restNonce;

const SCAN_STATS_CHECK_INTERVAL = Math.max( 1000, Math.min( edacpFullSiteScanApp.scanSpeed, 10000 ) );

debug( __( 'SCAN_STATS_CHECK_INTERVAL:', 'accessibility-checker-pro' ) + ' ' + SCAN_STATS_CHECK_INTERVAL );

const FORCE_SCAN_RESTART_IN_MINUTES = 10;

let scanStartButton;
let scanResetButton;
let stopScanButton;
let progressBarComplete;
let progressBarPercent;
let progressComplete;
let progressTotal;
let title;
let lead;
let bar;
let count;

const leadLabel =
	__( 'Checking the full site may take some time to complete. This page must be open for the scan to run.', 'accessibility-checker-pro' );

const labels = {
	new: {
		title: __( 'Full Site Accessibility Check', 'accessibility-checker-pro' ),
		status: '',
		lead: '',
	},
	canceled: {
		title: __( 'Full Site Accessibility Check', 'accessibility-checker-pro' ),
		status: '',
		lead: '',
	},
	queued: {
		title: __( 'Check Queued', 'accessibility-checker-pro' ),
		status: '',
		lead: __( 'The check has been queued and will begin shortly.', 'accessibility-checker-pro' ) + ' ' + leadLabel,
	},
	'js-scan-running': {
		title: __( 'Check In Progress', 'accessibility-checker-pro' ),
		lead: sprintf(
			'%1$s %2$s',
			__( 'The check is in progress.', 'accessibility-checker-pro' ),
			leadLabel
		),
	},
	completed: {
		title: __( 'Check Complete!', 'accessibility-checker-pro' ),
		lead: '',
	},
};

let jsScanStarted = false;
let maxUsedJs = 0;
let maxTotalJs = 0;
let maxJsHeap = 0;

const postData = async ( url = '', data = {} ) => {
	const response = await fetch( url, {
		headers: {
			'X-WP-Nonce': WP_REST_NONCE,
			'Content-Type': 'application/json',
		},
		method: 'POST',
		body: JSON.stringify( data ),
	} );
	return response.json();
};

const getData = async ( url = '' ) => {
	debug( sprintf( __( 'getData: %s', 'accessibility-checker-pro' ), url ) );
	try {
		const res = await fetch( url, {
			headers: {
				'X-WP-Nonce': WP_REST_NONCE,
				'Content-Type': 'application/json',
			},
			method: 'GET',
		} );
		const result = await res.json();
		debug( __( 'Got data:', 'accessibility-checker-pro' ) + ' ' + JSON.stringify( result ) );
		return result;
	} catch ( error ) {
		debug( sprintf( __( 'Error during GET request to %s', 'accessibility-checker-pro' ), url ) );
		return {};
	}
};

const show = ( e, display = 'block' ) => {
	if ( e ) {
		e.style.display = display;
	}
};

const hide = ( e ) => {
	if ( e ) {
		e.style.display = 'none';
	}
};

const setHtml = ( e, html ) => {
	if ( e ) {
		e.innerHTML = html;
	}
};

const getScanStats = () => {
	if ( window.performance && window.performance.memory ) {
		const memoryInfo = window.performance.memory;

		const usedJs = parseInt( memoryInfo.usedJSHeapSize / ( 1024 * 1024 ) );
		const totalJs = parseInt( memoryInfo.totalJSHeapSize / ( 1024 * 1024 ) );
		const jsHeap = parseInt( memoryInfo.jsHeapSizeLimit / ( 1024 * 1024 ) );

		if ( usedJs > maxUsedJs ) {
			maxUsedJs = usedJs;
		}
		if ( totalJs > maxTotalJs ) {
			maxTotalJs = totalJs;
		}
		if ( jsHeap > maxJsHeap ) {
			maxJsHeap = jsHeap;
		}

		const usage =
			usedJs +
			'/' +
			maxUsedJs +
			' | ' +
			totalJs +
			'/' +
			maxTotalJs +
			' | ' +
			jsHeap +
			'/' +
			maxJsHeap;

		if (
			( typeof edacpFullSiteScanApp !== 'undefined' && edacpFullSiteScanApp.scanStats?.js?.state ) &&
			edacpFullSiteScanApp.scanStats.js.state === 'js-scan-running' &&
			edacpFullSiteScanApp.scanStats.js.complete / 5 === parseInt( edacpFullSiteScanApp.scanStats.js.complete / 5 )
		) {
			debug( sprintf( __( 'Memory: %s', 'accessibility-checker-pro' ), usage ) );
		}

		if ( totalJs / jsHeap >= 0.8 ) {
			// reload page if using 80% of memory.
			window.location.reload( true );
		}
	}

	getData( API_URL + '/scan-stats' )
		.then( ( data ) => {
			if ( data.code !== 'rest_no_route' ) {
				if ( data.data !== undefined ) {
					if ( typeof ( edacpFullSiteScanApp ) === 'undefined' ) {
						edacpFullSiteScanApp = {};
					}
					edacpFullSiteScanApp.scanStats = data.data.scanStats;
					updateScanProgress();

					setTimeout( () => {
						getScanStats();
					}, SCAN_STATS_CHECK_INTERVAL );
				}
			} else {
				debug( __( 'rest route scan-stats is not setup.', 'accessibility-checker-pro' ) );

				showNotice( {
					msg: __( 'Whoops! It looks like there was a problem connecting to the Rest Api. Please refresh the page to try again.', 'accessibility-checker-pro' ),
					type: 'error',
				} );
			}
		} )
		.catch( () => {
			debug( __( 'rest route scan-stats is not setup.', 'accessibility-checker-pro' ) );

			showNotice( {
				msg: __( 'Whoops! It looks like there was a problem connecting to the Rest Api. Please refresh the page to try again.', 'accessibility-checker-pro' ),
				type: 'error',
			} );
		} );
};

const initUI = () => {
	scanStartButton = document.querySelector( '.edacp-scan-start-btn' );
	scanResetButton = document.querySelector( '.edacp-scan-reset-btn' );
	stopScanButton = document.querySelector( '.edacp-scan-stop-btn' );
	title = document.querySelector( '.edacp-scan-progress-title' );
	lead = document.querySelector( '.edacp-scan-progress-lead' );
	bar = document.querySelector( '.edacp-scan-progress-bar' );
	count = document.querySelector( '.edacp-scan-progress-count' );
	progressBarComplete = document.querySelector( '.edacp-scan-progress-bar-complete' );
	progressBarPercent = document.querySelector( '.edacp-scan-progress-bar-percentage' );
	progressComplete = document.querySelector( '.edacp-scan-progress-complete' );
	progressTotal = document.querySelector( '.edacp-scan-progress-total' );

	show( scanStartButton, 'inline-block' );
	hide( scanResetButton );
	hide( stopScanButton ); // Updated reference
	show( title );
	show( lead );
	hide( bar );
	hide( count );

	//show(progressBarComplete, 'inline-block');
	//show(progressBarPercent, 'inline-block');
	//show(progressComplete, 'inline-block');
	//show(progressTotal, 'inline-block');

	if ( scanStartButton ) {
		scanStartButton.addEventListener( 'click', function() {
			this.disabled = true;

			postData(
				edacpFullSiteScanApp.edacpApiUrl + '/start-new-scan',
				{}
			).finally( () => {
				window.location.reload();
			} );
		} );
	}

	if ( scanResetButton ) {
		scanResetButton.addEventListener( 'click', function() {
			this.disabled = true;

			postData(
				edacpFullSiteScanApp.edacpApiUrl + '/cancel-current-scan'
			).then( () => {
				window.location.reload();
			} );
		} );
	}

	if ( stopScanButton ) {
		stopScanButton.addEventListener( 'click', function() {
			postData(
				edacpFullSiteScanApp.edacpApiUrl + '/cancel-current-scan',
				{}
			).then( ( ) => {
				window.location.reload();
			} );
		} );
	}
};

const updateScanProgress = () => {
	const setProgress = function( percentage, animate = true ) {
		if ( animate ) {
			progressBarComplete.style.transition = 'width 1s';
		} else {
			progressBarComplete.style.transition = 'width 0s';
		}

		progressBarComplete.style.width = percentage + '%';
		setHtml( progressBarPercent, percentage + '%' );
	};

	const updateProgress = function( completeCount, failedCount, totalCount ) {
		let progress = 0;
		if ( totalCount > 0 ) {
			progress = Math.floor(
				( ( completeCount ) / totalCount ) * 100
			);
		}

		progressBarComplete.style.transition = 'width 1s';
		progressBarComplete.style.width = progress + '%';

		setHtml( progressBarPercent, progress + '%' );
		setHtml( progressComplete, completeCount );
	};

	if (
		typeof edacpFullSiteScanApp === 'undefined' ||
		typeof edacpFullSiteScanApp.scanStats === 'undefined'
	) {
		return;
	}

	let data;
	if (
		edacpFullSiteScanApp.scanStats.state === 'js-scan-running' ||
		edacpFullSiteScanApp.scanStats.state === 'js-scan-complete' ||
		edacpFullSiteScanApp.scanStats.state === 'completed' ||
		edacpFullSiteScanApp.scanStats.state === 'pre-completed' ) {
		data = edacpFullSiteScanApp.scanStats.js;

		if ( typeof data.state === 'undefined' ) {
			return;
		}
	} else {
		data = { state: 'new' };
	}

	switch ( data.state ) {
		case 'new':
			jsScanStarted = false;

			hide( bar );
			hide( count );
			hide( stopScanButton );
			show( scanResetButton, 'inline-block' );

			setHtml( title, labels.new.title );
			setHtml( lead, labels.new.lead );

			hide( document.getElementById( 'fail-accordion' ) );

			break;

		case 'canceled':
			jsScanStarted = false;

			hide( bar );
			hide( count );
			hide( stopScanButton );
			hide( scanResetButton );

			setHtml( title, labels.canceled.title );
			setHtml( lead, labels.canceled.lead );
			break;

		case 'queued':
			hide( bar );
			hide( count );
			show( stopScanButton, 'inline-block' );
			hide( scanResetButton );

			setHtml( title, labels.queued.title );
			setHtml( lead, labels.queued.lead );

			setProgress( 0, false );

			break;

		case 'js-scan-running':
		case 'pre-completed':
			show( bar );
			show( count );
			show( stopScanButton, 'inline-block' );
			hide( scanResetButton );

			setHtml( title, labels[ 'js-scan-running' ].title );
			setHtml( lead, labels[ 'js-scan-running' ].lead );

			if ( ! jsScanStarted ) {
				jsScanStarted = true;
				setProgress( 0, false );
			} else {
				updateProgress( data.complete, data.failed, data.total );
			}

			break;

		case 'js-scan-complete':
			setProgress( 100 );
			setCompletedScanMessage( data );

			show( bar );
			show( count );
			hide( stopScanButton );
			hide( scanResetButton );

			break;

		case 'completed':
			setProgress( 100 );
			setHtml( title, labels.completed.title );
			setCompletedScanMessage( data );

			hide( bar );
			hide( count );
			show( scanResetButton, 'inline-block' );
			hide( stopScanButton );

			// If the fails accordion is still hidden then fetch fails and try update it.
			const failAccordion = document.getElementById( 'fail-accordion' );
			if ( failAccordion && failAccordion.getAttribute( 'data-rendered-from-php' ) === 'true' ) {
				getData( API_URL + '/get-scan-failed-posts' )
					.then( ( failsData ) => {
						if ( failsData.code !== 'rest_no_route' ) {
							if ( failsData.data !== undefined && failsData.data?.failedScannedPages?.length ) {
								fillFailsAccordion( failsData.data.failedScannedPages );
							} else {
								// set the data-rendered-from-php attribute to false since we tried to render via JS.
								failAccordion.setAttribute( 'data-rendered-from-php', 'false' );
							}
						}
					} );
			}

			break;

		default:
			hide( bar );
			hide( count );
			show( scanResetButton, 'inline-block' );
			break;
	}

	setHtml(
		progressComplete,
		data.complete //+ data.failed
	);

	setHtml( progressTotal, data.total );
};

export const init = () => {
	initUI();

	// eslint-disable-next-line no-unused-vars
	getScanStats();

	setTimeout(
		function() {
			window.location.reload( true );
		},
		FORCE_SCAN_RESTART_IN_MINUTES * 1000 * 60
	);

	const cleanupOrphanedIframes = () => {
		const iframes = document.querySelectorAll( 'iframe[data-timestamp]' );
		const now = Date.now();
		iframes.forEach( ( iframe ) => {
			const lastActive = parseInt( iframe.getAttribute( 'data-timestamp' ), 10 );
			if ( isNaN( lastActive ) || now - lastActive > 30000 ) { // 30 seconds
				debug( sprintf( __( 'Cleaning up orphaned iframe: %s', 'accessibility-checker-pro' ), iframe.id ) );
				iframe.remove();
			}
		} );
	};

	// Periodically clean up orphaned iframes
	setInterval( cleanupOrphanedIframes, 5000 );
};

const setCompletedScanMessage = ( data ) => {
	const completionMessage = `
		<div style="display:block; text-align: center; margin: 15px 0;">
			<p style="font-size: 16px; margin-bottom: 10px;">
				<strong>${ sprintf( __( 'Scan completed on %s.', 'accessibility-checker-pro' ), data.date_formatted ) }</strong>
			</p>
			<p style="margin-bottom: 5px;">
				${ sprintf( __( '%1$s pages were checked successfully out of %2$s total pages.', 'accessibility-checker-pro' ), data.complete - data.failed, data.total ) }
			</p>
			${ data.failed > 0 ? `<p style=\"margin-bottom: 5px;\">${ sprintf( __( '%s pages failed to complete scanning.', 'accessibility-checker-pro' ), data.failed ) }</p>` : '' }
		</div>
	`;
	setHtml( lead, completionMessage );
};
